/**
 * SSO (Single Sign-On) Utility
 * Handles authentication and user information retrieval via hylid-bridge
 */

// Type definitions for SSO
export type UserScope =
    | "USER_NAME"
    | "USER_LNAME"
    | "USER_MNAME"
    | "USER_DOB"
    | "USER_ID"
    | "USER_IDNO"
    | "USER_MOBILE";

export interface UserScopeValue {
    key: UserScope;
    value: string;
}

export interface SSOSuccessResponse {
    kycAllowed: UserScopeValue[];
    kycRejected: UserScope[];
}

export interface SSOErrorResponse {
    error: number;
    errorMessage: string;
}

export interface UserInfo {
    firstName?: string;
    lastName?: string;
    middleName?: string;
    dateOfBirth?: string;
    userId?: string;
    idNumber?: string;
    mobile?: string;
}

// Error codes mapping
export const SSO_ERROR_CODES = {
    UNSUPPORTED_CURRENCY: 4,
    IDENTITY_VALIDATION_FAILED: 7,
    EMPTY_SCOPES_LIST: 10,
    OPERATION_FAILED: 102,
    SCOPES_NOT_ALLOWED: 103,
    CANCELED_BY_USER: 201,
} as const;

export const SSO_ERROR_MESSAGES: Record<number, string> = {
    4: "Unsupported currency code",
    7: "Identity Validation failed.",
    10: "Received empty list of scopes",
    102: "Operation Failed",
    103: "Scopes not allowed",
    201: "Canceled by user",
};

/**
 * Check if hylid-bridge is available in the environment
 */
export function isHylidBridgeAvailable(): boolean {
    if (typeof window === 'undefined') return false;

    try {
        // In M-Pesa environment, hylid-bridge is either injected or available via the package
        const w = window as any;
        return !!(w.hylidBridge || w.hylid || w.JSBridge || w.AlipayJSBridge);
    } catch {
        return false;
    }
}

/**
 * Request authentication from M-Pesa App
 * Returns an auth code which might be required to initialize the session.
 */
export async function requestAuth(): Promise<{ authCode: string } | SSOErrorResponse> {
    return new Promise(async (resolve, reject) => {
        console.log('[SSO] 🔐 Starting requestAuth...');
        try {
            const hylidBridge = await import('hylid-bridge');
            const bridgeCall = hylidBridge.call || (window as any).call;

            if (typeof bridgeCall === 'function') {
                bridgeCall('auth', {
                    success: function (res: { authCode: string }) {
                        console.log('[SSO] ✅ Auth Success:', res);
                        resolve(res);
                    },
                    fail: function (res: SSOErrorResponse) {
                        console.error('[SSO] ❌ Auth Fail:', res);
                        reject(res);
                    }
                });
            } else {
                reject({ error: 102, errorMessage: 'Bridge not found' });
            }
        } catch (err) {
            reject({ error: 102, errorMessage: 'Auth import failed' });
        }
    });
}

/**
 * Request user scopes from M-Pesa App via SSO
 * @param scopes - Array of user scopes to request
 * @returns Promise with user information or error
 */
export async function requestUserScopes(
    scopes: UserScope[]
): Promise<SSOSuccessResponse | SSOErrorResponse> {
    return new Promise(async (resolve, reject) => {
        console.log('[SSO] 📡 Starting requestUserScopes with:', scopes);

        try {
            // Diagnostic: Check global bridge availability
            const w = window as any;
            console.log('[SSO] Debug - Window properties:', {
                hylidBridge: !!w.hylidBridge,
                hylid: !!w.hylid,
                JSBridge: !!w.JSBridge,
                AlipayJSBridge: !!w.AlipayJSBridge,
                call: typeof w.call,
                alert: typeof w.alert,
                bridgeVersion: w.hylidBridge?.version || 'unknown'
            });

            // Import the bridge from the package
            console.log('[SSO] 📦 Attempting to import hylid-bridge package...');
            const hylidBridge = await import('hylid-bridge');

            const bridgeCall = hylidBridge.call || w.call;
            const bridgeAlert = hylidBridge.alert || w.alert;

            if (typeof bridgeCall === 'function') {
                console.log('[SSO] 🚀 Found bridgeCall function. Invoking "userScopes"...');

                bridgeCall('userScopes', {
                    scopes,
                    success: function (res: SSOSuccessResponse) {
                        console.log('[SSO] ✅ Success callback triggered:', res);
                        resolve(res);
                    },
                    fail: function (res: SSOErrorResponse) {
                        console.error('[SSO] ❌ Fail callback triggered. Full response:', JSON.stringify(res, null, 2));

                        // Check for specific error codes
                        if (res.error === 4 || (res.errorMessage && res.errorMessage.toLowerCase().includes('denied'))) {
                            console.error('[SSO] 🔐 PERMISSION ISSUE: "JSAPI call denied" (4). Please check M-Pesa Developer Portal API Whitelist.');
                        } else if (res.error === 102) {
                            console.error('[SSO] ⚙️ OPERATION FAILED (102).');
                            console.error('[SSO] 💡 SUGGESTION: This often means "auth" must be called first.');
                        }

                        if (typeof bridgeAlert === 'function') {
                            try {
                                bridgeAlert({
                                    content: `SSO Error: ${res.errorMessage || 'Operation Failed'} (Code: ${res.error})`,
                                });
                            } catch (alertError) {
                                console.error('[SSO] Failed to show bridge alert:', alertError);
                            }
                        }

                        reject(res);
                    },
                });
                return;
            } else {
                console.warn('[SSO] bridgeCall is not a function:', typeof bridgeCall);
            }
        } catch (err: any) {
            console.error('[SSO] ⚠️ Catch block error:', err);
        }

        // Fallback/Mock for development
        console.warn('[SSO] 🛠️ Falling back to mock data...');
        resolve({
            kycAllowed: [
                { key: "USER_NAME", value: "John (Mock)" },
                { key: "USER_LNAME", value: "Doe (Mock)" },
                { key: "USER_MOBILE", value: "+251900000000" },
            ],
            kycRejected: [],
        });
    });
}

/**
 * Parse SSO response into a user-friendly UserInfo object
 * @param response - SSO success response
 * @returns Parsed user information
 */
export function parseUserInfo(response: SSOSuccessResponse): UserInfo {
    const userInfo: UserInfo = {};

    response.kycAllowed.forEach((item) => {
        switch (item.key) {
            case "USER_NAME":
                userInfo.firstName = item.value;
                break;
            case "USER_LNAME":
                userInfo.lastName = item.value;
                break;
            case "USER_MNAME":
                userInfo.middleName = item.value;
                break;
            case "USER_DOB":
                userInfo.dateOfBirth = item.value;
                break;
            case "USER_ID":
                userInfo.userId = item.value;
                break;
            case "USER_IDNO":
                userInfo.idNumber = item.value;
                break;
            case "USER_MOBILE":
                userInfo.mobile = item.value;
                break;
        }
    });

    return userInfo;
}

/**
 * Get full user name from UserInfo
 * @param userInfo - User information object
 * @returns Full name string
 */
export function getFullName(userInfo: UserInfo): string {
    const parts = [
        userInfo.firstName,
        userInfo.middleName,
        userInfo.lastName,
    ].filter(Boolean);

    return parts.join(' ') || 'Guest User';
}

/**
 * Request all available user scopes
 * @returns Promise with complete user information
 */
export async function requestAllUserScopes(): Promise<UserInfo | null> {
    try {
        // 1. Try to request authentication first to initialize the session
        console.log('[SSO] Step 1: Requesting Auth...');
        try {
            const authRes = await requestAuth();
            console.log('[SSO] Step 1 Result:', authRes);
        } catch (authErr) {
            console.warn('[SSO] Auth Step 1 failed, but continuing to userScopes:', authErr);
        }

        // 2. Try requesting only the most essential scopes
        console.log('[SSO] Step 2: Requesting userScopes...');
        const response = await requestUserScopes([
            "USER_NAME",
            "USER_LNAME",
            "USER_MOBILE",
        ]);

        // Check if response is an error
        if ('error' in response) {
            console.error('[SSO] Error during requestAllUserScopes:', response);
            return null;
        }

        return parseUserInfo(response);
    } catch (error) {
        console.error('[SSO] Critical failure in requestAllUserScopes:', error);
        return null;
    }
}

/**
 * Get error message from error code
 * @param errorCode - SSO error code
 * @returns Error message
 */
export function getErrorMessage(errorCode: number): string {
    return SSO_ERROR_MESSAGES[errorCode] || 'Unknown error occurred';
}

// M-Pesa Login API types
export interface MpesaLoginRequest {
    firstName?: string;
    lastName?: string;
    middleName?: string;
    mobile?: string;
    dateOfBirth?: string;
    userId?: string;
    idNumber?: string;
}

export interface MpesaLoginResponse {
    token: string;
}

/**
 * Send M-Pesa user info to backend for authentication
 * @param userInfo - User information from M-Pesa SSO
 * @returns Promise with token
 * 
 * API Endpoint: POST /mpesa/login
 * - Mobile number is required
 * - Other fields are optional
 * - If user exists: returns existing user token
 * - If user doesn't exist: creates new user with role "Customer User Access" and returns token
 * - Token expires in 1 day
 */
export async function mpesaLogin(userInfo: UserInfo): Promise<MpesaLoginResponse> {
    console.log('[SSO] 🔐 Sending user info to backend for M-Pesa login...');
    
    // Validate mobile number is present (required)
    if (!userInfo.mobile) {
        throw new Error('Mobile number is required');
    }

    // Prepare request body with only provided fields
    const requestBody: any = {
        mobile: userInfo.mobile,
    };

    // Add optional fields only if they exist
    if (userInfo.firstName) requestBody.firstName = userInfo.firstName;
    if (userInfo.lastName) requestBody.lastName = userInfo.lastName;
    if (userInfo.middleName) requestBody.middleName = userInfo.middleName;
    if (userInfo.dateOfBirth) requestBody.dateOfBirth = userInfo.dateOfBirth;
    if (userInfo.userId) requestBody.userId = userInfo.userId;
    if (userInfo.idNumber) requestBody.idNumber = userInfo.idNumber;

    console.log('[SSO] 📤 Request body:', requestBody);
    
    const apiUrl = import.meta.env.VITE_API_URL || import.meta.env.NEXT_PUBLIC_API_URL || ''
    const res = await fetch(`${apiUrl}/mpesa/login`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
    });

    // Handle error responses
    if (!res.ok) {
        let errorMessage = "M-Pesa login failed";
        
        try {
            const errorData = await res.json();
            // API returns error in format: { "error": "error message" }
            errorMessage = errorData.error || errorMessage;
        } catch {
            // If response is not JSON, try to get text
            const errorText = await res.text().catch(() => "");
            errorMessage = errorText || errorMessage;
        }

        console.error('[SSO] ❌ M-Pesa login failed:', {
            status: res.status,
            statusText: res.statusText,
            error: errorMessage,
        });
        
        throw new Error(errorMessage);
    }

    // Parse successful response
    const data = await res.json();
    
    // Validate response has token
    if (!data.token) {
        console.error('[SSO] ❌ No token in response:', data);
        throw new Error('No token received from server');
    }

    console.log('[SSO] ✅ M-Pesa login successful, token received');
    return data;
}

/**
 * Complete M-Pesa authentication flow:
 * 1. Get user info from M-Pesa SSO
 * 2. Send to backend API
 * 3. Save token to cookies
 * 4. Return token
 * 
 * @param loginCallback - Optional callback to integrate with AuthProvider's login function
 */
export async function authenticateWithMpesa(
    loginCallback?: (token: string) => void
): Promise<MpesaLoginResponse | null> {
    try {
        console.log('[SSO] 🚀 Starting complete M-Pesa authentication flow...');
        
        // Step 1: Get user info from M-Pesa SSO
        const userInfo = await requestAllUserScopes();
        
        if (!userInfo) {
            console.error('[SSO] ❌ Failed to get user info from M-Pesa');
            return null;
        }
        
        console.log('[SSO] 📋 User info retrieved:', userInfo);
        
        // Step 2: Send to backend and get token
        const loginResponse = await mpesaLogin(userInfo);
        
        // Step 3: Save token to cookies and call login callback if provided
        if (loginResponse.token) {
            // Import js-cookie dynamically to avoid SSR issues
            const Cookies = (await import('js-cookie')).default;
            Cookies.set('qetero.token', loginResponse.token, { expires: 7, path: '/' });
            console.log('[SSO] 🍪 Token saved to cookies');
            
            // Call the login callback if provided (integrates with AuthProvider)
            if (loginCallback) {
                loginCallback(loginResponse.token);
            }
        }
        
        console.log('[SSO] ✅ Authentication complete. Token received.');
        return loginResponse;
    } catch (error) {
        console.error('[SSO] ❌ M-Pesa authentication failed:', error);
        throw error;
    }
}

// Hook for M-Pesa authentication with loading state
export interface UseMpesaLoginResult {
    login: (loginCallback?: (token: string) => void) => Promise<MpesaLoginResponse | null>;
    isLoading: boolean;
    error: Error | null;
}

/**
 * React hook for M-Pesa authentication
 * Provides loading state and error handling
 * 
 * @example
 * ```tsx
 * const { login, isLoading, error } = useMpesaLogin();
 * const { login: authLogin } = useAuth();
 * 
 * const handleLogin = async () => {
 *   const result = await login(authLogin);
 *   if (result) {
 *     router.push('/booking/service');
 *   }
 * };
 * ```
 */
export function useMpesaLogin(): UseMpesaLoginResult {
    // Note: This is a utility that can be used with useState from the calling component
    // Import { useState } from 'react' in your component
    let isLoading = false;
    let error: Error | null = null;

    const login = async (loginCallback?: (token: string) => void): Promise<MpesaLoginResponse | null> => {
        isLoading = true;
        error = null;
        
        try {
            const result = await authenticateWithMpesa(loginCallback);
            isLoading = false;
            return result;
        } catch (err) {
            error = err instanceof Error ? err : new Error('M-Pesa login failed');
            isLoading = false;
            throw error;
        }
    };

    return { login, isLoading, error };
}

/**
 * Standalone function to perform M-Pesa login with callbacks for loading state
 * Use this if you need more control over loading state in your component
 */
export async function performMpesaLogin(options: {
    onStart?: () => void;
    onSuccess?: (response: MpesaLoginResponse) => void;
    onError?: (error: Error) => void;
    onFinish?: () => void;
    loginCallback?: (token: string) => void;
}): Promise<MpesaLoginResponse | null> {
    const { onStart, onSuccess, onError, onFinish, loginCallback } = options;
    
    try {
        onStart?.();
        const result = await authenticateWithMpesa(loginCallback);
        
        if (result) {
            onSuccess?.(result);
        }
        
        return result;
    } catch (err) {
        const error = err instanceof Error ? err : new Error('M-Pesa login failed');
        onError?.(error);
        throw error;
    } finally {
        onFinish?.();
    }
}
